import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useSearchParams } from "react-router";
import { useAuth } from "@/auth/useAuth";
import { sessionDiagnosticsAccess } from "@/features/session-diagnostics/access/session-diagnostics-access";
import {
    PayloadCodecDeps,
    sessionDiagnosticsApi,
    sessionFilters,
} from "@/features/session-diagnostics/api/session-diagnostics.api";
import {
    SegmentPollContext,
    SegmentPollDeps,
    SegmentPollStore,
    segmentPollStore,
} from "@/features/session-diagnostics/hooks/use-segment-poll";

// The production wiring of the live stream. The decoded payload stays raw here: it is
// cleaned inside the cursor (`segmentCursor.apply`), the only door to the player.
export const sessionStreamDeps = (workspaceId: string, codec?: PayloadCodecDeps): SegmentPollDeps => ({
    poll: (segments) => sessionDiagnosticsApi.poll(workspaceId, segments),
    decode: (payload) => sessionDiagnosticsApi.decodeEvents(payload, codec),
    setTimer: (callback, ms) => setTimeout(callback, ms),
    clearTimer: (handle) => clearTimeout(handle),
});

const createStore = (workspaceId: string): SegmentPollStore => segmentPollStore.create(sessionStreamDeps(workspaceId));

// The mosaic and the detail are children of this route, so they share ONE poll stream:
// opening a session reuses the tile's stream instead of starting a second capture read.
const SessionStreamScope: React.FC<{ workspaceId: string }> = ({ workspaceId }) => {
    const [store, setStore] = useState<SegmentPollStore | null>(null);
    useEffect(() => {
        const created = createStore(workspaceId);
        setStore(created);
        return () => created.dispose();
    }, [workspaceId]);
    if (store === null) return null;
    return (
        <SegmentPollContext.Provider value={store}>
            <Outlet />
        </SegmentPollContext.Provider>
    );
};

export const SessionDiagnosticsRoute: React.FC = () => {
    const { state } = useAuth();
    const [params] = useSearchParams();
    const workspaceId = sessionDiagnosticsAccess.workspaceFor(sessionFilters.parse(params).filters.workspaceId);
    if (sessionDiagnosticsAccess.canOpen(state) === false || workspaceId === null) return <Navigate to="/command-center" replace />;
    return <SessionStreamScope workspaceId={workspaceId} />;
};

export default SessionDiagnosticsRoute;
